import { graphql } from 'gatsby'
import React, { useMemo } from 'react'

import {
  FooterCompanyMenuFragmentFragment,
  FooterHelpMenuFragmentFragment,
  FooterQuestionMenuFragmentFragment,
  FooterResourcesMenuFragmentFragment,
} from '../../../../graphql/gatsby'
import useSiteSettings from '../../../../hooks/useSiteSettings'
import { useSanityRoleAndContextBasedContent } from '../../../../lib/sanity'
import { isNotNullAnd } from '../../../../utils/collectionTools'
import { FooterCountrySelector } from '../FooterCountrySelector'
import { FooterLinksGroup } from '../FooterLinksGroup'
import { FooterLinksMobile } from '../FooterLinksMobile'
import * as Styled from './styled'

type CompanyMenuEntry = NonNullable<
  NonNullable<FooterCompanyMenuFragmentFragment['footerCompanyMenu']>[number]
>

type HelpMenuEntry = NonNullable<
  NonNullable<FooterHelpMenuFragmentFragment['footerHelpMenu']>[number]
>

type QuestionMenuEntry = NonNullable<
  NonNullable<FooterQuestionMenuFragmentFragment['footerQuestionMenu']>[number]
>

type ResourcesMenuEntry = NonNullable<
  NonNullable<FooterResourcesMenuFragmentFragment['footerResourcesMenu']>[number]
>

export type FooterMenuEntry =
  | CompanyMenuEntry
  | HelpMenuEntry
  | QuestionMenuEntry
  | ResourcesMenuEntry

export interface FooterLinksGroupDetails {
  heading?: string
  id: string
  subHeading?: string
  links: FooterMenuEntry[]
}

export interface FooterLinksProps {
  className?: string
}

export const FooterLinks: React.FC<FooterLinksProps> = ({ className }) => {
  const siteSettings = useSiteSettings()
  const { shouldDisplayContent } = useSanityRoleAndContextBasedContent()
  const menuEntries = useMemo(() => {
    const {
      footerCompanyMenu: companyMenuEntries,
      footerHelpMenu: helpMenuEntries,
      footerQuestionMenu: questionMenuEntries,
      footerResourcesMenu: resourcesMenuEntries,
    } = siteSettings || {}
    return {
      companyLinks: companyMenuEntries?.filter(isNotNullAnd(shouldDisplayContent)) ?? [],
      helpLinks: helpMenuEntries?.filter(isNotNullAnd(shouldDisplayContent)) ?? [],
      questionLinks: questionMenuEntries?.filter(isNotNullAnd(shouldDisplayContent)) ?? [],
      resourcesLinks: resourcesMenuEntries?.filter(isNotNullAnd(shouldDisplayContent)) ?? [],
    }
  }, [shouldDisplayContent, siteSettings])

  const companySection: FooterLinksGroupDetails = {
    heading: 'Company',
    id: 'company',
    links: menuEntries.companyLinks,
  }
  const helpSection: FooterLinksGroupDetails = {
    heading: 'Help',
    id: 'help',
    links: menuEntries.helpLinks,
  }
  const questionsSection: FooterLinksGroupDetails = {
    subHeading: 'Have a question?',
    id: 'question',
    links: menuEntries.questionLinks,
  }
  const resourcesSection: FooterLinksGroupDetails = {
    heading: 'Resources',
    id: 'resources',
    links: menuEntries.resourcesLinks,
  }

  return (
    <div className={className}>
      <Styled.Container>
        <Styled.Row>
          <Styled.Column>
            {companySection.links.length > 0 && (
              <FooterLinksGroup heading={companySection.heading} links={companySection.links} />
            )}
          </Styled.Column>
          <Styled.Column>
            {helpSection.links.length > 0 && (
              <FooterLinksGroup heading={helpSection.heading} links={helpSection.links} />
            )}
            {questionsSection.links.length > 0 && (
              <FooterLinksGroup
                subHeading={questionsSection.subHeading}
                links={questionsSection.links}
              />
            )}
          </Styled.Column>
          <Styled.Column>
            {resourcesSection.links.length > 0 && (
              <FooterLinksGroup heading={resourcesSection.heading} links={resourcesSection.links} />
            )}
          </Styled.Column>
        </Styled.Row>
        <FooterLinksMobile
          groups={[
            companySection,
            {
              heading: 'Help',
              id: 'help',
              links: [
                ...helpSection.links,
                {
                  text: 'Have a question?',
                },
                ...questionsSection.links,
              ],
            },
            resourcesSection,
          ]}
        />
        <Styled.Row>
          <Styled.Column />
          <Styled.Column />
          <Styled.Column>
            <FooterCountrySelector />
          </Styled.Column>
        </Styled.Row>
      </Styled.Container>
    </div>
  )
}

export const footerCompanyMenu = graphql`
  fragment footerCompanyMenuFragment on SanitySiteSettings {
    footerCompanyMenu {
      name
      link {
        ...LinkFragment
      }
      roleBasedContent {
        enabled
        roles
      }
      contextBasedContent {
        enabled
        contexts
      }
    }
  }
`

export const footerHelpMenu = graphql`
  fragment footerHelpMenuFragment on SanitySiteSettings {
    footerHelpMenu {
      name
      link {
        ...LinkFragment
      }
      roleBasedContent {
        enabled
        roles
      }
      contextBasedContent {
        enabled
        contexts
      }
    }
  }
`

export const footerQuestionMenu = graphql`
  fragment footerQuestionMenuFragment on SanitySiteSettings {
    footerQuestionMenu {
      name
      link {
        ...LinkFragment
      }
      roleBasedContent {
        enabled
        roles
      }
      contextBasedContent {
        enabled
        contexts
      }
    }
  }
`

export const footerResourcesMenu = graphql`
  fragment footerResourcesMenuFragment on SanitySiteSettings {
    footerResourcesMenu {
      name
      link {
        ...LinkFragment
      }
      roleBasedContent {
        enabled
        roles
      }
      contextBasedContent {
        enabled
        contexts
      }
    }
  }
`
